import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { ActionCustomerMenu } from "@/components/Holizontal/ActionCustomerMenu";
import { useTranslation } from "react-i18next";

import { Check, ChevronDown, Loader2, Search, ArrowLeft, ArrowRight,User,
  Phone,
  CreditCard,
  MapPin,
  Users,
  RefreshCw, } from "lucide-react";

// shadcn ui (if you already use it)
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";

/** ======================
 *  API PATHS (EDIT HERE)
 *  ====================== */
const API_BRANCHES = "/actions/customers/move-profile/branches"; // GET
const API_BDOS = "/actions/customers/move-profile/bdos"; // GET ?branch_id=1
const API_CUSTOMERS = "/actions/customers/move-profile/customers"; // GET ?from_bdo_id=..&q=..&cursor=..&anchor_id=..
const API_MOVE = "/actions/customers/move-profile/move"; // POST

const PER_PAGE = 10;

/** ======================
 *  Types
 *  ====================== */
type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type CustomerRow = {
  id: number;
  fname?: string;
  mname?: string;
  sname?: string;
  phone_number?: string;
  national_id?: string;
  BRANCH?: string;
  USERNAME?: string;
  USERNAME2?: string;
  CREATEDBY?: string;
  regDate?: string;
  created_at?: string;
};

type CursorMeta = {
  per_page: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
};

type CustomersRes = {
  data: CustomerRow[];
  meta: CursorMeta;
  anchor_id: number | null;
};

type ApiMessageRes = {
  type: "success" | "error";
  message: string;
  moved?: number;
};

/** ======================
 *  Helpers
 *  ====================== */
function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function fullName(r: CustomerRow) {
  const parts = [r.fname, r.mname, r.sname].filter(Boolean);
  return parts.length ? parts.join(" ") : `Customer #${r.id}`;
}

function bdoLabel(r: CustomerRow) {
  const a = r.USERNAME ?? "-";
  const b = r.USERNAME2 ? ` & ${r.USERNAME2}` : "";
  return `${a}${b}`;
}

/** ======================
 *  Searchable Select (shadcn)
 *  ====================== */
function SearchSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled,
  className,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  className?: string;
}) {
  const selected = options.find((o) => o.value === value)?.label ?? "";

  return (
    <div className={cn("space-y-1", className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>

      <Popover>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            className={cn(
              "h-10 w-full rounded-md border border-border bg-background px-3 text-sm",
              "inline-flex items-center justify-between gap-2",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <span className={cn("truncate text-left", !value && "text-muted-foreground")}>
              {value ? selected : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => onChange(o.value)
                   // setOpen(false)
                  }
                  className="flex items-center justify-between"
                >
                  <span className="truncate">{o.label}</span>
                  {value === o.value && <Check className="h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** ======================
 *  Page
 *  ====================== */
export default function MoveCustomerProfilePage() {
  // options
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);

    const { t } = useTranslation();
  // selection
  const [branchId, setBranchId] = useState<string>("");
  const [fromBdoId, setFromBdoId] = useState<string>("");
  const [toBdoId, setToBdoId] = useState<string>("");

  // list/search
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);

  // customers
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  // selection of customers
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // move flow
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moving, setMoving] = useState(false);

  // result alert
  const [resultOpen, setResultOpen] = useState(false);
  const [resultType, setResultType] = useState<"success" | "error">("success");
  const [resultMsg, setResultMsg] = useState("");

  // scroll to table top
  const tableTopRef = useRef<HTMLDivElement | null>(null);
  const scrollToTopOfTable = () => {
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  /** ===== initial load: branches ===== */
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch<{ branches: Branch[] }>(API_BRANCHES);
        setBranches(res.branches ?? []);
      } catch (e) {
        // ignore here; page can still load with other calls
      }
    })();
  }, []);

  /** ===== load bdos when branch changes ===== */
  useEffect(() => {
    setBdos([]);
    setFromBdoId("");
    setToBdoId("");
    setRows([]);
    setSelectedIds(new Set());
    setCursor(null);
    setAnchorId(null);

    if (!branchId) return;

    (async () => {
      try {
        const res = await apiFetch<{ bdos: Bdo[] }>(`${API_BDOS}?branch_id=${branchId}`);
        setBdos(res.bdos ?? []);
      } catch (e) {
        setBdos([]);
      }
    })();
  }, [branchId]);

  /** ===== reset paging when filters change ===== */
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
    setSelectedIds(new Set());
  }, [fromBdoId, qDebounced]);

  /** ===== load customers for fromBdo ===== */
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set("per_page", String(PER_PAGE));
    if (fromBdoId) p.set("from_bdo_id", fromBdoId);
    if (qDebounced.trim()) p.set("q", qDebounced.trim());
    if (cursor) p.set("cursor", cursor);
    if (anchorId != null) p.set("anchor_id", String(anchorId));
    return p.toString();
  }, [fromBdoId, qDebounced, cursor, anchorId]);

  useEffect(() => {
    if (!fromBdoId) {
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
      setAnchorId(null);
      return;
    }

    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await apiFetch<CustomersRes>(`${API_CUSTOMERS}?${queryString}`);
        setRows(res.data ?? []);
        setNextCursor(res.meta?.next_cursor ?? null);
        setPrevCursor(res.meta?.prev_cursor ?? null);
        setAnchorId((prev) => (prev == null ? res.anchor_id ?? null : prev));
      } catch (e: any) {
        setRows([]);
        setNextCursor(null);
        setPrevCursor(null);
        setLoadError(e?.message ?? "Failed to load customers");
      } finally {
        setLoading(false);
      }
    })();
  }, [queryString, fromBdoId]);

  /** ===== selection helpers ===== */
  const allVisibleIds = useMemo(() => rows.map((r) => r.id), [rows]);

  const isAllChecked = useMemo(() => {
    if (rows.length === 0) return false;
    return allVisibleIds.every((id) => selectedIds.has(id));
  }, [rows, allVisibleIds, selectedIds]);

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (isAllChecked) {
      allVisibleIds.forEach((id) => next.delete(id));
    } else {
      allVisibleIds.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
  };

  const toggleOne = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  /** ===== pagination bottom ===== */
  const goPrev = () => {
    if (!prevCursor || loading) return;
    setCursor(prevCursor);
    scrollToTopOfTable();
  };
  const goNext = () => {
    if (!nextCursor || loading) return;
    setCursor(nextCursor);
    scrollToTopOfTable();
  };

  /** ===== move ===== */
  const canMove =
    !!branchId &&
    !!fromBdoId &&
    !!toBdoId &&
    fromBdoId !== toBdoId &&
    selectedIds.size > 0 &&
    !moving;

  const openConfirm = () => {
    if (!canMove) return;
    setConfirmOpen(true);
  };

  const doMove = async () => {
    setConfirmOpen(false);
    setMoving(true);

    try {
      const payload = {
        branch_id: Number(branchId),
        from_bdo_id: Number(fromBdoId),
        to_bdo_id: Number(toBdoId),
        customer_ids: Array.from(selectedIds),
      };

      const res = await apiFetch<ApiMessageRes>(API_MOVE, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setResultType(res.type ?? "success");
      setResultMsg(res.message ?? "Done");
      setResultOpen(true);

      if ((res.type ?? "success") === "success") {
        // refresh current page
        setSelectedIds(new Set());
        setCursor(null);
        setAnchorId(null);
      }
    } catch (e: any) {
      setResultType("error");
      setResultMsg(e?.message ?? "Failed to move customers");
      setResultOpen(true);
    } finally {
      setMoving(false);
    }
  };

  /** ===== options for selects ===== */
  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: String(b.id), label: b.name })),
    [branches]
  );

  const bdoOptions = useMemo(
    () => bdos.map((b) => ({ value: String(b.id), label: b.name })),
    [bdos]
  );

  const toBdoOptions = useMemo(
    () => bdoOptions.filter((o) => o.value !== fromBdoId),
    [bdoOptions, fromBdoId]
  );

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      {/* Header */}
      

       <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <RefreshCw className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight"> {t("move.title")}</h1>
              <p className="text-xs text-muted-foreground"> Move selected customers from one BDO to another (within the same branch). </p>
            </div>
          </div>

           <div className="flex items-center gap-2 sm:justify-end">
          <Button
            onClick={openConfirm}
            disabled={!canMove}
            className="h-10 px-4"
          >
            {moving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Moving...
              </>
            ) : (
              "Move Selected"
            )}
          </Button>
        </div>
        </div>
      </header>

      {/* Menu */}
      <ActionCustomerMenu />

      {/* Controls */}
      <Card className="border-border">
        <CardContent className="p-3 sm:p-4 space-y-3">
          {/* 3 searchable dropdowns in one row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SearchSelect
              label="Branch"
              placeholder="Select branch..."
              value={branchId}
              onChange={(v) => setBranchId(v)}
              options={branchOptions}
            />

            <SearchSelect
              label="From BDO"
              placeholder={!branchId ? "Select branch first..." : "Select BDO..."}
              value={fromBdoId}
              onChange={(v) => {
                setFromBdoId(v);
                setToBdoId(""); // reset to
              }}
              options={bdoOptions}
              disabled={!branchId}
            />

            <SearchSelect
              label="To BDO"
              placeholder={!fromBdoId ? "Select From BDO first..." : "Select destination BDO..."}
              value={toBdoId}
              onChange={(v) => setToBdoId(v)}
              options={toBdoOptions}
              disabled={!fromBdoId}
            />
          </div>

          {/* Search + summary */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
            <div className="w-full md:w-96">
              <div className="text-xs font-medium text-muted-foreground mb-1">Search customer</div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Name / phone / ID..."
                  className="pl-9"
                  disabled={!fromBdoId}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-sm">
              <div className="px-3 py-1 rounded-full bg-muted">
                Visible: <span className="font-semibold">{rows.length}</span>
              </div>
              <div className="px-3 py-1 rounded-full bg-muted">
                Selected: <span className="font-semibold">{selectedIds.size}</span>
              </div>
              {loading && (
                <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground">
                  Loading...
                </div>
              )}
            </div>
          </div>

          {loadError && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {loadError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Listing */}
      <Card className="border-border">
        <CardContent className="p-3 sm:p-4">
          <div ref={tableTopRef} />

          {/* Desktop table */}
          <div className="hidden md:block">
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold w-[50px]">
                      <input
                        type="checkbox"
                        checked={isAllChecked}
                        onChange={toggleAll}
                        disabled={rows.length === 0}
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Phone</th>
                    <th className="px-4 py-3 font-semibold">National ID</th>
                    <th className="px-4 py-3 font-semibold">Branch</th>
                    <th className="px-4 py-3 font-semibold">BDO</th>
                  </tr>
                </thead>

                <tbody>
                  {!loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                        {fromBdoId ? "No customers found for this BDO." : "Select Branch and From BDO to load customers."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(r.id)}
                            onChange={() => toggleOne(r.id)}
                          />
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-medium">{fullName(r)}</div>
                          <div className="text-xs text-muted-foreground">ID: {r.id}</div>
                        </td>

                        <td className="px-4 py-3">{r.phone_number ?? "-"}</td>
                        <td className="px-4 py-3">{r.national_id ?? "-"}</td>
                        <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>
                        <td className="px-4 py-3">{bdoLabel(r)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {!loading && rows.length === 0 ? (
              <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
                {fromBdoId ? "No customers found for this BDO." : "Select Branch and From BDO to load customers."}
              </div>
            ) : (
              rows.map((r) => (
               <div key={r.id} className="rounded-xl border border-border bg-background p-3">
  <div className="flex items-start justify-between gap-3">

    {/* LEFT CONTENT */}
    <div className="min-w-0 flex-1">

      {/* Name */}
      <div className="font-semibold truncate inline-flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground shrink-0" />
        {fullName(r)}
      </div>

      {/* Phone + ID */}
      <div className="mt-2 text-xs text-muted-foreground grid gap-1">

        <div className="flex items-center gap-2">
          <Phone className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{r.phone_number ?? "-"}</span>
        </div>

        <div className="flex items-center gap-2">
          <CreditCard className="h-3.5 w-3.5 shrink-0" />
          <span>ID: {r.id}</span>
        </div>

      </div>

      {/* Branch + BDO */}
      <div className="mt-2 text-xs text-muted-foreground grid gap-1">

        <div className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{r.BRANCH ?? "-"}</span>
        </div>

        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{bdoLabel(r)}</span>
        </div>

      </div>
    </div>

    {/* CHECKBOX */}
    <div className="shrink-0 pt-1">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        checked={selectedIds.has(r.id)}
        onChange={() => toggleOne(r.id)}
      />
    </div>

  </div>
</div>
              ))
            )}
          </div>

          {/* Bottom pagination */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="outline"
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className="inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </Button>

            <Button
              variant="outline"
              onClick={goNext}
              disabled={!nextCursor || loading}
              className="inline-flex items-center gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Move</DialogTitle>
            <DialogDescription>
              You are about to move <b>{selectedIds.size}</b> customer(s) to the selected BDO.
              This will reassign their profile ownership.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
            <div><span className="text-muted-foreground">From BDO:</span> <b>{bdoOptions.find(x => x.value === fromBdoId)?.label ?? "-"}</b></div>
            <div className="mt-1"><span className="text-muted-foreground">To BDO:</span> <b>{toBdoOptions.find(x => x.value === toBdoId)?.label ?? "-"}</b></div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doMove} disabled={moving}>
              {moving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Moving...
                </>
              ) : (
                "Yes, Move"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result alert */}
      <AlertDialog open={resultOpen} onOpenChange={setResultOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resultType === "success" ? "Success" : "Error"}
            </AlertDialogTitle>
            <AlertDialogDescription>{resultMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setResultOpen(false)}>OK</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
